import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import {
  getQuotesForDeal,
  logQuoteDecline,
} from '@/services/quotes.service.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { ApiError } from '@/lib/api.js';
import { DECLINE_REASONS, requiresDetail } from '@/lib/declineReasons.js';
import { toDate } from '@/lib/date-display.js';

// Sheet for logging a carrier decline. POST
// /api/tracon/command-center/quotes/:quoteId/decline sets the quote's own
// status='declined' and records WHAT THE CARRIER SAID on the market ladder.
//
// THE REASONS ARE THE SAME CHECKBOXES THE LADDER USES (CC PROMPT 23 Part A).
// This sheet used to collect free prose into quotes.decline_reason while
// MarketLadderPanel's DeclineDialog collected canonical values into
// submission_markets, so the same act of logging a decline produced two
// incompatible records depending on which button the broker happened to press.
// One vocabulary, one store, either door.
//
// Trigger: DealActionBar "Log decline" button. The broker picks from
// the deal's existing quotes (only those NOT already declined). For
// a deal with no quote rows yet (carrier in submitted/awaiting/draft
// state only), the sheet shows an empty-state — the broker can't
// decline what doesn't exist; that's a different action (drop draft).

const DECLINABLE_STATUSES = new Set(['received', 'presented']);

export default function LogDeclineSheet({ dealId, open, onOpenChange, onLogged }) {
  const [quotes, setQuotes] = useState(null);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !dealId) return;
    let cancelled = false;
    setLoadingQuotes(true);
    setSelectedId(null);
    setReasons([]);
    setDetail('');
    getQuotesForDeal(dealId)
      .then((r) => {
        if (cancelled) return;
        const rows = (r?.quotes || r?.items || [])
          .filter((q) => DECLINABLE_STATUSES.has(q.status))
          .sort((a, b) =>
            (toDate(b.received_at || b.created_at)?.getTime() ?? 0) -
            (toDate(a.received_at || a.created_at)?.getTime() ?? 0)
          );
        setQuotes(rows);
        if (rows.length === 1) setSelectedId(rows[0].id);
      })
      .catch(() => {
        if (!cancelled) setQuotes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingQuotes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dealId]);

  const toggleReason = (v) =>
    setReasons((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedId || !reasonValid) return;
    setSubmitting(true);
    try {
      const r = await logQuoteDecline(selectedId, { reasons, detail: detail.trim() || null });
      // A carrier name that will not resolve to a market cannot join the
      // ladder. The quote is declined either way; say so rather than letting
      // the toast imply the shopping record captured it.
      if (r?.market_recorded === false) {
        toast.error(`Quote declined, but not recorded on the ladder: ${r.market_error || 'carrier did not resolve to a market'}`);
      } else {
        toast.success('Decline logged');
      }
      if (typeof onLogged === 'function') onLogged({ quoteId: selectedId, reasons, detail });
      onOpenChange?.(false);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Couldn't log decline: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  // Detail is required for 'other' ONLY, never for not_stated: there is nothing
  // to explain about a silence.
  const reasonValid = reasons.length > 0 && (!requiresDetail(reasons) || detail.trim().length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto md:max-w-md">
        <SheetHeader>
          <SheetTitle>Log carrier decline</SheetTitle>
          <SheetDescription>
            Marks the quote declined and records what the carrier said on the market
            ladder. At least one reason is required.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Quote
            </legend>
            {loadingQuotes ? (
              <p className="text-sm text-text-dim">Loading quotes…</p>
            ) : !quotes || quotes.length === 0 ? (
              <p className="rounded-card border border-border bg-bg/40 p-3 text-sm text-text-muted">
                No declinable quotes on this deal. Decline only applies to received or
                presented quotes — drafts/submissions go through a different flow.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {quotes.map((q) => (
                  <li key={q.id}>
                    <label
                      className={`flex cursor-pointer items-start gap-2 rounded-card border p-2.5 text-sm transition ${
                        selectedId === q.id
                          ? 'border-accent bg-accent-soft'
                          : 'border-border bg-surface hover:border-border-strong'
                      }`}
                    >
                      <input
                        type="radio"
                        name="quote"
                        value={q.id}
                        checked={selectedId === q.id}
                        onChange={() => setSelectedId(q.id)}
                        className="mt-1 accent-accent"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-semibold text-text">{q.carrier || '—'}</span>
                          <Badge variant="outline" className="text-[9px]">
                            {q.status?.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-text-muted">
                          {q.premium ? formatCurrency(q.premium) : null}
                          {q.received_at || q.created_at ? (
                            <span className="ml-2 text-text-dim">
                              {formatDate(q.received_at || q.created_at)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>

          <fieldset className="space-y-1.5">
            <legend className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Why did they pass?
            </legend>
            {DECLINE_REASONS.map((r) => (
              <label key={r.value} className="flex cursor-pointer items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={reasons.includes(r.value)}
                  onChange={() => toggleReason(r.value)}
                  className="accent-accent"
                />
                {r.label}
              </label>
            ))}
            <textarea
              rows={2}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={requiresDetail(reasons) ? 'Detail (required for Other)' : 'Detail (optional): what exactly did they say?'}
              maxLength={500}
              className={cn(
                'mt-2 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
                'placeholder:text-text-dim',
                'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
                'resize-none'
              )}
            />
          </fieldset>

          <SheetFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedId || !reasonValid || submitting}
            >
              {submitting ? 'Logging…' : 'Log decline'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
